import { useEffect, useRef, useState } from 'react';
import { notification } from 'antd';
import { staffAddOtpService } from '../../../services/userServices';

// Director "add staff" OTP (invite / create / re-invite), shared by the three screens.
// request() emails the code and opens EmailConfirmationModal; submit() runs the real call with it.

const RESEND_COOLDOWN_SECONDS = 30;
const FALLBACK_TTL_SECONDS = 300;

const sameRequest = (a, b) =>
    !!a && !!b && a.action === b.action && a.campCode === b.campCode
    && [...a.emails].map(e => String(e).trim().toLowerCase()).sort().join(',')
        === [...b.emails].map(e => String(e).trim().toLowerCase()).sort().join(',');

// ownEmail: the logged-in user's email, which is where the API sends the code.
const useStaffAddOtp = ({ ownEmail } = {}) => {
    const [visible, setVisible] = useState(false);
    const [otp, setOtp] = useState('');
    const [error, setError] = useState('');
    const [sending, setSending] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [expiry, setExpiry] = useState(0);
    const [cooldown, setCooldown] = useState(0);
    const [mustResend, setMustResend] = useState(false);
    const lastRequest = useRef(null);
    const intervalRef = useRef(null);

    useEffect(() => () => clearInterval(intervalRef.current), []);

    // One interval drives both the code's life and the shorter resend lock.
    const startTimers = seconds => {
        clearInterval(intervalRef.current);
        setExpiry(seconds);
        setCooldown(Math.min(RESEND_COOLDOWN_SECONDS, seconds));
        intervalRef.current = setInterval(() => {
            setCooldown(prev => (prev > 0 ? prev - 1 : 0));
            setExpiry(prev => {
                if (prev <= 1) {
                    clearInterval(intervalRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const forceResend = message => {
        clearInterval(intervalRef.current);
        setExpiry(0);
        setCooldown(0);
        setMustResend(true);
        setError(message);
        setOtp('');
    };

    // payload: { action, campCode, emails }. A still-valid code for the same request just reopens the modal.
    const request = (payload, isResend = false) => {
        if (!isResend && sameRequest(payload, lastRequest.current) && expiry > 0 && !mustResend) {
            setError('');
            setVisible(true);
            return Promise.resolve(true);
        }
        setSending(true);
        setError('');
        const fail = res => {
            const message = res?.data?.error?.responseMessage || 'We could not send the verification code. Please try again.';
            if (isResend) setError(message);
            else notification.open({ description: message });
            return false;
        };
        return staffAddOtpService(payload).then(res => {
            setSending(false);
            if (res?.data?.statusCode !== 1) return fail(res);
            lastRequest.current = payload;
            startTimers(res.data.responseData?.otpExpiresInSeconds || FALLBACK_TTL_SECONDS);
            setMustResend(false);
            setOtp('');
            setVisible(true);
            return true;
        }).catch(() => {
            setSending(false);
            return fail();
        });
    };

    // apiCall(code) returns the axios promise. Resolves to the response for success and non-OTP
    // errors (modal closed, the screen shows them); resolves to null when a code error stays in the modal.
    const submit = apiCall => {
        if (!/^\d{4}$/.test(otp)) {
            setError('Enter the 4-digit code from the email.');
            return Promise.resolve(null);
        }
        setError('');
        setSubmitting(true);
        return Promise.resolve(apiCall(otp)).then(res => {
            setSubmitting(false);
            const errorCode = res?.data?.error?.errorCode;
            if (res?.data?.statusCode !== 1 && errorCode === 10) {
                setError(res.data.error.responseMessage || 'That code is not correct.');
                setOtp('');
                return null;
            }
            if (res?.data?.statusCode !== 1 && errorCode === 20) {
                forceResend('That code has expired. Request a new one to continue.');
                return null;
            }
            if (res?.data?.statusCode === 1) {
                clearInterval(intervalRef.current);
                setExpiry(0);
                lastRequest.current = null;
            }
            setVisible(false);
            return res;
        }).catch(() => {
            setSubmitting(false);
            setError('Something went wrong. Please try again.');
            return null;
        });
    };

    const close = () => setVisible(false);

    const modalProps = {
        email: ownEmail || 'your email',
        otp,
        onOtpChange: value => { setOtp(value); setError(''); },
        error,
        expirySeconds: expiry,
        cooldownSeconds: cooldown,
        mustResend,
        sending,
        submitting,
        onResend: () => lastRequest.current && request(lastRequest.current, true),
        onClose: close,
        title: 'Confirm it’s you',
        confirmLabel: 'Confirm',
        footnote: 'Close this to go back — nothing is sent or created until you confirm the code.',
    };

    return { visible, sending, submitting, request, submit, close, modalProps };
};

export default useStaffAddOtp;
